import React, { useCallback, useEffect, useState } from "react";
import AppLayout from "@/components/AppLayout";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, Search, GitMerge, Undo2, Archive } from "lucide-react";
import { DIVISIONS, divisionLabel } from "@/lib/roles";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

const MERGE_ROLES = ["super_admin", "founder", "ops_manager", "hr_admin"];
const EMPTY_FORM = { name: "", email: "", phone: "", company: "", division: "academy", tags: "", notes: "" };

export default function Contacts() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [division, setDivision] = useState("all");
  const [scope, setScope] = useState("active"); // active | archived
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  // Duplicate-detected state
  const [dupOpen, setDupOpen] = useState(false);
  const [dupMatches, setDupMatches] = useState([]);
  const [pendingPayload, setPendingPayload] = useState(null);

  // Merge state
  const [mergeMode, setMergeMode] = useState(false);
  const [mergeTarget, setMergeTarget] = useState(null);
  const [mergeSource, setMergeSource] = useState(null);

  const canMerge = user && MERGE_ROLES.includes(user.role);

  const load = useCallback(async () => {
    const params = {};
    if (q) params.q = q;
    if (division !== "all") params.division = division;
    if (scope === "archived") params.only_archived = true;
    const r = await api.get("/contacts", { params });
    setItems(r.data);
  }, [q, division, scope]);
  useEffect(() => { load(); }, [load]);

  const buildPayload = () => ({
    ...form,
    tags: form.tags.split(",").map(t => t.trim()).filter(Boolean),
  });

  const submit = async (force = false) => {
    setSaving(true);
    try {
      const payload = pendingPayload || buildPayload();
      await api.post("/contacts", payload, { params: force ? { force: true } : {} });
      toast.success("Contact created");
      setOpen(false); setDupOpen(false);
      setForm(EMPTY_FORM); setPendingPayload(null); setDupMatches([]);
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail?.matches?.length) {
        setDupMatches(detail.matches);
        setPendingPayload(buildPayload());
        setDupOpen(true);
      } else {
        toast.error(typeof detail === "string" ? detail : (detail?.message || "Create failed"));
      }
    } finally { setSaving(false); }
  };

  const archive = async (id) => {
    if (!window.confirm("Archive this contact? You can restore it later.")) return;
    await api.delete(`/contacts/${id}`);
    toast.success("Archived");
    load();
  };
  const restore = async (id) => {
    await api.post(`/contacts/${id}/restore`);
    toast.success("Restored");
    load();
  };

  const startMerge = (c) => {
    if (!mergeTarget) {
      setMergeTarget(c);
      toast.info(`Kept "${c.name}" as the primary. Now select another contact to merge INTO it.`);
    } else if (!mergeSource && c.id !== mergeTarget.id) {
      setMergeSource(c);
    }
  };
  const cancelMerge = () => { setMergeMode(false); setMergeTarget(null); setMergeSource(null); };
  const doMerge = async () => {
    if (!mergeTarget || !mergeSource) return;
    try {
      await api.post("/contacts/merge", { target_id: mergeTarget.id, source_id: mergeSource.id });
      toast.success(`Merged "${mergeSource.name}" into "${mergeTarget.name}"`);
      cancelMerge(); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Merge failed"); }
  };

  return (
    <AppLayout title="Contacts">
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="flex items-center gap-2 bg-white border border-[var(--miro-border)] rounded-lg px-3 py-2 flex-1 min-w-[220px] max-w-md">
          <Search size={15} className="text-[var(--miro-text-muted)]"/>
          <input data-testid="contacts-search-input" className="bg-transparent outline-none text-sm flex-1"
                 placeholder="Search contacts…" value={q} onChange={(e) => setQ(e.target.value)}/>
        </div>
        <Select value={division} onValueChange={setDivision}>
          <SelectTrigger data-testid="contacts-filter-division" className="w-40 bg-white"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All divisions</SelectItem>
            {DIVISIONS.map(d => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={scope} onValueChange={setScope}>
          <SelectTrigger data-testid="contacts-filter-scope" className="w-36 bg-white"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="archived">Archived</SelectItem>
          </SelectContent>
        </Select>
        {canMerge && !mergeMode && scope === "active" && (
          <Button data-testid="contacts-merge-mode-btn" variant="outline"
                  onClick={() => setMergeMode(true)}>
            <GitMerge size={14} className="mr-1"/> Merge contacts
          </Button>
        )}
        {mergeMode && (
          <Button data-testid="contacts-merge-cancel-btn" variant="outline" onClick={cancelMerge}>
            Cancel merge
          </Button>
        )}
        <div className="ml-auto">
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setForm(EMPTY_FORM); setPendingPayload(null); }}}>
            <DialogTrigger asChild>
              <Button data-testid="new-contact-btn" style={{ background: "var(--miro-navy)" }}><Plus size={16} className="mr-1"/> New contact</Button>
            </DialogTrigger>
            <DialogContent className="bg-white">
              <DialogHeader><DialogTitle className="font-display text-2xl text-[var(--miro-navy)]">New contact</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><Label>Name *</Label><Input data-testid="new-contact-name" value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})} className="mt-1"/></div>
                <div><Label>Email</Label><Input data-testid="new-contact-email" type="email" value={form.email} onChange={(e)=>setForm({...form,email:e.target.value})} className="mt-1"/></div>
                <div><Label>Phone</Label><Input data-testid="new-contact-phone" value={form.phone} onChange={(e)=>setForm({...form,phone:e.target.value})} className="mt-1"/></div>
                <div><Label>Company</Label><Input data-testid="new-contact-company" value={form.company} onChange={(e)=>setForm({...form,company:e.target.value})} className="mt-1"/></div>
                <div>
                  <Label>Division</Label>
                  <Select value={form.division} onValueChange={(v)=>setForm({...form,division:v})}>
                    <SelectTrigger data-testid="new-contact-division" className="mt-1"><SelectValue/></SelectTrigger>
                    <SelectContent>{DIVISIONS.map(d => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="col-span-2"><Label>Tags (comma-separated)</Label><Input data-testid="new-contact-tags" value={form.tags} onChange={(e)=>setForm({...form,tags:e.target.value})} className="mt-1"/></div>
                <div className="col-span-2"><Label>Notes</Label><Textarea data-testid="new-contact-notes" rows={2} value={form.notes} onChange={(e)=>setForm({...form,notes:e.target.value})} className="mt-1"/></div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button data-testid="new-contact-submit" disabled={!form.name.trim() || saving} onClick={() => submit(false)} style={{ background: "var(--miro-navy)" }}>
                  {saving ? "Creating…" : "Create"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {mergeMode && (
        <div data-testid="contacts-merge-banner" className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm">
          <div className="font-semibold text-[var(--miro-navy)]">Merge mode</div>
          <div className="text-[var(--miro-text-secondary)] text-xs mt-0.5">
            Pick the PRIMARY contact first, then the DUPLICATE to merge into it. Child records will be re-linked.
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-3">
            <span className="text-xs">Primary: <span className="font-semibold text-[var(--miro-navy)]">{mergeTarget?.name || "—"}</span></span>
            <span className="text-xs">Duplicate: <span className="font-semibold text-[var(--miro-navy)]">{mergeSource?.name || "—"}</span></span>
            {mergeTarget && mergeSource && (
              <Button size="sm" data-testid="contacts-merge-confirm-btn" onClick={doMerge}
                      style={{ background: "var(--miro-green)", color: "white" }}>
                Confirm merge
              </Button>
            )}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {items.length === 0 && (
          <div className="col-span-full text-center py-16 text-[var(--miro-text-muted)]">
            {scope === "archived" ? "No archived contacts." : "No contacts yet. Create your first contact to get started."}
          </div>
        )}
        {items.map(c => {
          const isTarget = mergeTarget?.id === c.id;
          const isSource = mergeSource?.id === c.id;
          return (
          <div key={c.id} data-testid={`contact-card-${c.id}`}
               onClick={() => mergeMode && startMerge(c)}
               className={`miro-card p-5 ${mergeMode ? "cursor-pointer hover:ring-2 hover:ring-[var(--miro-blue)]" : ""} ${isTarget ? "ring-2 ring-green-500" : ""} ${isSource ? "ring-2 ring-orange-500" : ""}`}>
            <div className="flex items-start gap-3">
              <div className="w-11 h-11 rounded-lg flex items-center justify-center font-display font-bold text-white flex-shrink-0"
                   style={{ background: "linear-gradient(140deg,#0EA5FF 0%,#041B46 100%)" }}>
                {c.name[0]?.toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display font-bold text-[var(--miro-navy)] truncate">{c.name}</div>
                <div className="text-xs text-[var(--miro-text-secondary)] truncate">{c.company || "—"}</div>
                {c.archived && (
                  <div className="text-[10px] uppercase tracking-wider text-orange-600 mt-0.5">
                    {c.merged_into ? "merged" : "archived"}
                  </div>
                )}
              </div>
              {!mergeMode && (scope === "active" ? (
                <button data-testid={`contact-delete-${c.id}`} onClick={(e) => { e.stopPropagation(); archive(c.id); }}
                        className="text-[var(--miro-text-muted)] hover:text-red-600 transition-colors" title="Archive">
                  <Archive size={15}/>
                </button>
              ) : (
                <button data-testid={`contact-restore-${c.id}`} onClick={(e) => { e.stopPropagation(); restore(c.id); }}
                        className="text-[var(--miro-text-muted)] hover:text-green-600 transition-colors" title="Restore">
                  <Undo2 size={15}/>
                </button>
              ))}
            </div>
            <div className="mt-3 space-y-1 text-sm">
              <div className="text-[var(--miro-text-secondary)] truncate">{c.email || "—"}</div>
              <div className="text-[var(--miro-text-secondary)]">{c.phone || "—"}</div>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-1.5">
              <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded"
                    style={{ background: "#E0F2FE", color: "#0284C7" }}>
                {divisionLabel(c.division || "academy")}
              </span>
              {(c.tags || []).map(t => (
                <span key={t} className="text-[10px] px-2 py-0.5 rounded bg-[var(--miro-bg)] border border-[var(--miro-border)] text-[var(--miro-text-secondary)]">
                  #{t}
                </span>
              ))}
            </div>
          </div>);
        })}
      </div>

      {/* Duplicate detected dialog */}
      <Dialog open={dupOpen} onOpenChange={setDupOpen}>
        <DialogContent className="bg-white" data-testid="contacts-duplicate-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-[var(--miro-navy)]">Possible duplicate found</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-[var(--miro-text-secondary)]">
            We found {dupMatches.length} existing contact{dupMatches.length !== 1 && "s"} matching this email or phone:
          </div>
          <div className="space-y-2 mt-2 max-h-56 overflow-auto">
            {dupMatches.map(m => (
              <div key={m.id} className="border border-[var(--miro-border)] rounded-lg p-3 text-sm">
                <div className="font-semibold text-[var(--miro-navy)]">{m.name}</div>
                <div className="text-xs text-[var(--miro-text-muted)]">{m.email || "—"} · {m.phone || "—"} · {m.company || "—"}</div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDupOpen(false); setPendingPayload(null); }}>
              Cancel
            </Button>
            <Button data-testid="contacts-duplicate-force"
                    onClick={() => submit(true)} disabled={saving} style={{ background: "var(--miro-navy)" }}>
              Create anyway
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
